import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PhoneIcon from '@mui/icons-material/Phone';
import SendIcon from '@mui/icons-material/Send';
import MailIcon from '@mui/icons-material/MarkEmailRead';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CaseActivityApi, {
  ActivityChannel,
  ActivityItem,
  ActivityKind,
  NOTE_TOPIC_LABELS,
  NoteTopic,
} from '../../api/CaseActivityApi';
import CaseEmailApi from '../../api/CaseEmailApi';
import MessageApi from '../../api/MessageApi';
import OfferPortalApi, { OfferSource, RecordOfferInput } from '../../api/OfferPortalApi';
import OfferRecordForm from '../CaseOfferPanel/OfferRecordForm';
import { describeAutoSend } from '../CaseOfferPanel/offerDisplay';

/**
 * Everything on a case in one list (Lloyd, 6 Oct 2026): notes, calls, emails and the client's
 * messages, newest first, with buttons to show one kind and a find box. Replaces the separate
 * Emails, Messages and Notes tabs (Lloyd, 7 Oct: "Remove.").
 *
 * Each row is marked with how it travelled, in his letters: P portal, E email, T text, C call.
 */

const CHANNEL_LETTER: Record<ActivityChannel, { letter: string; label: string }> = {
  portal: { letter: 'P', label: 'Portal' },
  email: { letter: 'E', label: 'Email' },
  text: { letter: 'T', label: 'Text' },
  call: { letter: 'C', label: 'Call' },
};

const KIND_FILTERS: { value: ActivityKind | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'note', label: 'Notes' },
  { value: 'call', label: 'Calls' },
  { value: 'email', label: 'Emails' },
  { value: 'message', label: 'Messages' },
];

const MAILBOX_LABEL: Record<string, string> = {
  court: 'CourtCommunication@',
  client: 'Client@',
  cdl: 'CDL mailbox',
};

const CALL_OUTCOME_LABEL = { spoke: 'Spoke', voicemail: 'Voicemail', 'no-answer': 'No answer' } as const;

/**
 * How an offer read from a note reached us, from what the note is. A call or a court call is a
 * phone offer, a virtual hearing a hearing one; anything else is left for staff to say.
 */
function offerSourceFor(item: ActivityItem): OfferSource {
  if (item.note?.topic === 'virtual_hearing') return 'hearing';
  if (item.kind === 'call' || item.note?.topic === 'court_called' || item.note?.topic === 'client_called') {
    return 'phone';
  }
  return 'other';
}

/** Long bodies are cut to this until opened, so one email does not push the rest off the screen. */
const PREVIEW_CHARS = 400;

function searchText(item: ActivityItem): string {
  return [
    item.title,
    item.body,
    item.author,
    item.email?.from,
    ...(item.email?.to ?? []),
    item.note?.topic ? NOTE_TOPIC_LABELS[item.note.topic] : null,
  ]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return minutes > 0 ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
}

const ChannelBadges: React.FC<{ channels: ActivityChannel[] }> = ({ channels }) => (
  <Stack direction="row" spacing={0.5}>
    {channels.map((c) => (
      <Tooltip key={c} title={CHANNEL_LETTER[c].label}>
        <Box
          component="span"
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          {CHANNEL_LETTER[c].letter}
        </Box>
      </Tooltip>
    ))}
  </Stack>
);

const ActivityBody: React.FC<{ body: string }> = ({ body }) => {
  const [open, setOpen] = useState(false);
  if (!body) return null;
  const long = body.length > PREVIEW_CHARS;
  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5, overflowWrap: 'anywhere' }}>
      {long && !open ? `${body.slice(0, PREVIEW_CHARS).trimEnd()}…` : body}
      {long && (
        <Link component="button" variant="body2" sx={{ ml: 1 }} onClick={() => setOpen(!open)}>
          {open ? 'Show less' : 'Show more'}
        </Link>
      )}
    </Typography>
  );
};

interface CaseActivityPanelProps {
  Ticket_guid: string;
  /** The client, for writing notes and messages. Absent when the case has no client attached. */
  User_guid?: string;
  /** Opens the Offer tab, where a recorded offer is reviewed and sent. */
  onOpenOffer?: () => void;
  /** Called when recording an offer moved the case stage, so the chart can refresh. */
  onStageMoved?: () => void;
}

type NoteDialog = { mode: 'create' } | { mode: 'edit'; item: ActivityItem };

const CaseActivityPanel: React.FC<CaseActivityPanelProps> = ({
  Ticket_guid,
  User_guid,
  onOpenOffer,
  onStageMoved,
}) => {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [kind, setKind] = useState<ActivityKind | 'all'>('all');
  const [query, setQuery] = useState('');

  const [noteDialog, setNoteDialog] = useState<NoteDialog | null>(null);
  const [noteForm, setNoteForm] = useState({ title: '', body: '', topic: '' as NoteTopic | '' });
  const [callOpen, setCallOpen] = useState(false);
  const [callForm, setCallForm] = useState({
    direction: 'outbound' as 'outbound' | 'inbound',
    outcome: 'spoke' as 'spoke' | 'voicemail' | 'no-answer',
    topic: '' as NoteTopic | '',
    body: '',
  });
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageBody, setMessageBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [mailboxMenu, setMailboxMenu] = useState<HTMLElement | null>(null);
  // "Record offer" from a note (Lloyd, 6 Oct 2026: notes "connected to offers").
  const [offerFrom, setOfferFrom] = useState<ActivityItem | null>(null);
  const [offerAutoSend, setOfferAutoSend] = useState(false);
  const [offerResult, setOfferResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await CaseActivityApi.list(Ticket_guid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: items.length };
    for (const item of items) c[item.kind] = (c[item.kind] ?? 0) + 1;
    return c;
  }, [items]);

  const visible = useMemo(() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    return items.filter((item) => {
      if (kind !== 'all' && item.kind !== kind) return false;
      if (words.length === 0) return true;
      const text = searchText(item);
      return words.every((w) => text.includes(w));
    });
  }, [items, kind, query]);

  /** Runs a write, then reloads, showing any failure in place. */
  const act = async (fn: () => Promise<void>, done: string) => {
    setSaving(true);
    setError(null);
    try {
      await fn();
      if (done) setInfo(done);
      await load();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openCreateNote = () => {
    setNoteForm({ title: '', body: '', topic: '' });
    setNoteDialog({ mode: 'create' });
  };

  const openEditNote = (item: ActivityItem) => {
    setNoteForm({ title: item.title ?? '', body: item.body, topic: item.note?.topic ?? '' });
    setNoteDialog({ mode: 'edit', item });
  };

  const saveNote = async () => {
    if (!noteDialog || !noteForm.body.trim()) return;
    const topic = noteForm.topic || null;
    // The API wants a title. A quick note needs none, so it takes the topic's name or "Note".
    const title = noteForm.title.trim() || (topic ? NOTE_TOPIC_LABELS[topic] : 'Note');
    const ok = await act(
      () =>
        noteDialog.mode === 'edit'
          ? CaseActivityApi.updateNote({
              UserNote_guid: noteDialog.item.note!.guid,
              UserNote_Title: title,
              UserNote_Desc: noteForm.body.trim(),
              UserNote_Topic: topic,
            })
          : CaseActivityApi.createNote({
              User_guid: User_guid!,
              Ticket_guid,
              UserNote_Title: title,
              UserNote_Desc: noteForm.body.trim(),
              UserNote_Topic: topic,
            }),
      noteDialog.mode === 'edit' ? 'Note updated.' : 'Note added.',
    );
    if (ok) setNoteDialog(null);
  };

  const saveCall = async () => {
    const who = callForm.topic === 'court_called' ? 'court' : 'client';
    const summary =
      callForm.direction === 'outbound'
        ? `Outbound call - ${callForm.outcome === 'spoke' ? `spoke with ${who}` : callForm.outcome === 'voicemail' ? 'left voicemail' : 'no answer'}`
        : `Inbound call - ${callForm.outcome === 'spoke' ? `spoke with ${who}` : callForm.outcome === 'voicemail' ? 'voicemail received' : 'missed'}`;
    const ok = await act(
      () =>
        CaseActivityApi.createNote({
          User_guid: User_guid!,
          Ticket_guid,
          UserNote_Title: summary,
          UserNote_Desc: callForm.body.trim() || summary,
          UserNote_Kind: 'call',
          UserNote_CallDirection: callForm.direction,
          UserNote_CallOutcome: callForm.outcome,
          UserNote_Topic: callForm.topic || null,
        }),
      'Call logged.',
    );
    if (ok) {
      setCallOpen(false);
      setCallForm({ direction: 'outbound', outcome: 'spoke', topic: '', body: '' });
    }
  };

  const sendMessage = async () => {
    if (!messageBody.trim()) return;
    const ok = await act(async () => {
      await MessageApi.sendMessage({ Message_Body: messageBody.trim(), User_guid: User_guid! });
    }, 'Message sent. It goes to the client’s portal, email and phone (if they agreed to texts).');
    if (ok) {
      setMessageOpen(false);
      setMessageBody('');
    }
  };

  const deleteNote = async (item: ActivityItem) => {
    if (!window.confirm('Delete this note?')) return;
    await act(() => CaseActivityApi.deleteNote(item.note!.guid), 'Note deleted.');
  };

  const fileNote = (item: ActivityItem) =>
    act(() => CaseActivityApi.updateNote({ UserNote_guid: item.note!.guid, Ticket_guid }), 'Note filed to this case.');

  const openRecordOffer = (item: ActivityItem) => {
    setOfferFrom(item);
    // Read when the dialog opens, so the form says truthfully whether Save also sends.
    OfferPortalApi.isClientAutoSendOn(Ticket_guid)
      .then(setOfferAutoSend)
      .catch(() => setOfferAutoSend(false));
  };

  const recordOffer = async (input: RecordOfferInput) => {
    if (!offerFrom?.note) return;
    const sourceNoteGuid = offerFrom.note.guid;
    const ok = await act(async () => {
      const { stageMove, autoSend } = await OfferPortalApi.recordOffer(Ticket_guid, { ...input, sourceNoteGuid });
      if (stageMove === 'moved') onStageMoved?.();
      setOfferResult(describeAutoSend(autoSend) ?? 'Offer recorded. Review and send it from the Offer tab.');
    }, '');
    if (ok) setOfferFrom(null);
  };

  const checkMailbox = async (mailbox: 'court' | 'client') => {
    setMailboxMenu(null);
    await act(async () => {
      const s = await CaseEmailApi.ingest(mailbox);
      setInfo(`Checked ${MAILBOX_LABEL[mailbox]}: ${s.stored} new email(s) tied to cases.`);
    }, '');
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={kind}
          onChange={(_, value) => value && setKind(value)}
          sx={{ flexWrap: 'wrap' }}
        >
          {KIND_FILTERS.map((f) => (
            <ToggleButton key={f.value} value={f.value}>
              {f.label} ({counts[f.value] ?? 0})
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Find on this case"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flex: 1, minWidth: 180 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreateNote} disabled={!User_guid}>
          Add note
        </Button>
        <Button size="small" variant="outlined" startIcon={<PhoneIcon />} onClick={() => setCallOpen(true)} disabled={!User_guid}>
          Log call
        </Button>
        <Button size="small" variant="outlined" startIcon={<SendIcon />} onClick={() => setMessageOpen(true)} disabled={!User_guid}>
          Message client
        </Button>
        <Button size="small" startIcon={<MailIcon />} onClick={(e) => setMailboxMenu(e.currentTarget)} disabled={saving}>
          Check mailbox
        </Button>
        <Menu anchorEl={mailboxMenu} open={!!mailboxMenu} onClose={() => setMailboxMenu(null)}>
          <MenuItem onClick={() => checkMailbox('court')}>CourtCommunication@</MenuItem>
          <MenuItem onClick={() => checkMailbox('client')}>Client@</MenuItem>
        </Menu>
      </Stack>

      {!User_guid && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This case has no client attached, so notes and messages cannot be added here.
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}
      {offerResult && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setOfferResult(null)}
          action={
            onOpenOffer && (
              <Button color="inherit" size="small" onClick={onOpenOffer}>
                Open Offer tab
              </Button>
            )
          }
        >
          {offerResult}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      ) : visible.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
          {items.length === 0 ? 'Nothing on this case yet.' : 'Nothing matches.'}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {visible.map((item) => (
            <ActivityRow
              key={item.id}
              item={item}
              Ticket_guid={Ticket_guid}
              disabled={saving}
              onEdit={openEditNote}
              onDelete={deleteNote}
              onFile={fileNote}
              onRecordOffer={openRecordOffer}
              onOpenOffer={onOpenOffer}
            />
          ))}
        </Stack>
      )}

      {/* Add or edit a note */}
      <Dialog open={!!noteDialog} onClose={() => setNoteDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{noteDialog?.mode === 'edit' ? 'Edit note' : 'Add note'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              size="small"
              label="Type"
              value={noteForm.topic}
              onChange={(e) => setNoteForm({ ...noteForm, topic: e.target.value as NoteTopic | '' })}
            >
              <MenuItem value="">Note</MenuItem>
              {(Object.keys(NOTE_TOPIC_LABELS) as NoteTopic[]).map((t) => (
                <MenuItem key={t} value={t}>
                  {NOTE_TOPIC_LABELS[t]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Title (optional)"
              value={noteForm.title}
              onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
            />
            <TextField
              label="Note"
              multiline
              minRows={4}
              value={noteForm.body}
              onChange={(e) => setNoteForm({ ...noteForm, body: e.target.value })}
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveNote} disabled={saving || !noteForm.body.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Log a call */}
      <Dialog open={callOpen} onClose={() => setCallOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log a call</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              size="small"
              label="Who"
              value={callForm.topic}
              onChange={(e) => setCallForm({ ...callForm, topic: e.target.value as NoteTopic | '' })}
            >
              <MenuItem value="">Client</MenuItem>
              <MenuItem value="client_called">Client called</MenuItem>
              <MenuItem value="court_called">Court called</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Direction"
              value={callForm.direction}
              onChange={(e) => setCallForm({ ...callForm, direction: e.target.value as 'outbound' | 'inbound' })}
            >
              <MenuItem value="outbound">We called them</MenuItem>
              <MenuItem value="inbound">They called us</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="What happened"
              value={callForm.outcome}
              onChange={(e) =>
                setCallForm({ ...callForm, outcome: e.target.value as 'spoke' | 'voicemail' | 'no-answer' })
              }
            >
              <MenuItem value="spoke">Spoke with them</MenuItem>
              <MenuItem value="voicemail">Left / received voicemail</MenuItem>
              <MenuItem value="no-answer">No answer</MenuItem>
            </TextField>
            <TextField
              label="Notes (optional)"
              multiline
              minRows={3}
              value={callForm.body}
              onChange={(e) => setCallForm({ ...callForm, body: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCallOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveCall} disabled={saving}>
            Log call
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record an offer from a note: the form prefilled from it, and the offer linked back to it. */}
      <Dialog open={!!offerFrom} onClose={() => setOfferFrom(null)} maxWidth="md" fullWidth>
        <DialogTitle>Record an offer from this note</DialogTitle>
        <DialogContent>
          {offerFrom && (
            <Box sx={{ mt: 1 }}>
              <OfferRecordForm
                key={offerFrom.id}
                onSubmit={recordOffer}
                saving={saving}
                initialSource={offerSourceFor(offerFrom)}
                initialNotes={offerFrom.body}
                autoSend={offerAutoSend}
                hideHeading
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfferFrom(null)} disabled={saving}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message the client */}
      <Dialog open={messageOpen} onClose={() => setMessageOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Message the client</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Goes to their portal and email, and to their phone if they have agreed to texts.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={sendMessage} disabled={saving || !messageBody.trim()}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const KIND_LABEL: Record<ActivityKind, string> = {
  note: 'Note',
  call: 'Call',
  email: 'Email',
  message: 'Message',
};

const ActivityRow: React.FC<{
  item: ActivityItem;
  Ticket_guid: string;
  disabled: boolean;
  onEdit: (item: ActivityItem) => void;
  onDelete: (item: ActivityItem) => void;
  onFile: (item: ActivityItem) => void;
  onRecordOffer: (item: ActivityItem) => void;
  onOpenOffer?: () => void;
}> = ({ item, Ticket_guid, disabled, onEdit, onDelete, onFile, onRecordOffer, onOpenOffer }) => {
  const note = item.note;
  // Only notes typed here can be changed; Dialpad, Clio and system rows are records of what happened.
  const editable = !!note && note.source === 'manual';
  const unfiled = !!note && note.ticketGuid === null;
  const otherCopy = !!note && note.ticketGuid !== null && note.ticketGuid !== Ticket_guid;
  // An offer is read from what someone wrote or heard: a typed note or a call, not a system or Clio row.
  const canRecordOffer = !!note && (note.source === 'manual' || note.source === 'dialpad') && !note.offerGuid;

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ChannelBadges channels={item.channels} />
          {item.direction && (
            <Tooltip title={item.direction === 'outbound' ? 'Sent by us' : 'Received'}>
              {item.direction === 'outbound' ? (
                <CallMadeIcon fontSize="small" color="action" />
              ) : (
                <CallReceivedIcon fontSize="small" color="action" />
              )}
            </Tooltip>
          )}
          <Chip label={KIND_LABEL[item.kind]} size="small" variant="outlined" />
          {note?.topic && <Chip label={NOTE_TOPIC_LABELS[note.topic]} size="small" color="primary" variant="outlined" />}
          {note?.source === 'dialpad' && <Chip label="Dialpad" size="small" />}
          {note?.source === 'clio' && <Chip label="From Clio" size="small" />}
          {item.message?.source === 'system' && <Chip label="Automatic" size="small" />}
          {unfiled && <Chip label="Client note, not filed to a case" size="small" color="warning" variant="outlined" />}
          {otherCopy && <Chip label="From a duplicate copy of this case" size="small" variant="outlined" />}
          {note?.offerGuid && (
            <Chip
              label="Offer recorded"
              size="small"
              color="success"
              onClick={onOpenOffer}
              clickable={!!onOpenOffer}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto !important' }}>
            {formatWhen(item.at)}
            {item.author ? ` · ${item.author}` : ''}
          </Typography>
          {editable && (
            <>
              <IconButton size="small" onClick={() => onEdit(item)} disabled={disabled} title="Edit">
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => onDelete(item)} disabled={disabled} title="Delete">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Stack>

        {item.title && (
          <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.75 }}>
            {item.title}
          </Typography>
        )}

        {item.email && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            From {item.email.from || 'unknown'} · To {item.email.to.join(', ') || 'unknown'} · via{' '}
            {MAILBOX_LABEL[item.email.mailbox] ?? item.email.mailbox}
            {item.email.mailboxAddress ? ` (${item.email.mailboxAddress})` : ''}
          </Typography>
        )}

        {note && item.kind === 'call' && (note.callOutcome || note.callDurationSeconds || note.callRecordingUrl) && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            {[
              note.callOutcome ? CALL_OUTCOME_LABEL[note.callOutcome] : null,
              note.callDurationSeconds ? formatDuration(note.callDurationSeconds) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
            {note.callRecordingUrl && (
              <>
                {' · '}
                <Link href={note.callRecordingUrl} target="_blank" rel="noopener noreferrer">
                  Recording
                </Link>
              </>
            )}
          </Typography>
        )}

        <ActivityBody body={item.body} />

        {item.email?.mailboxWarning && (
          <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}>
            {item.email.mailboxWarning}
          </Alert>
        )}
        {item.email?.cdlRefusal && (
          <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}>
            No answer was sent. {item.email.cdlRefusal}
          </Alert>
        )}
        {(canRecordOffer || (unfiled && editable)) && (
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            {canRecordOffer && (
              <Button size="small" onClick={() => onRecordOffer(item)} disabled={disabled}>
                Record offer
              </Button>
            )}
            {unfiled && editable && (
              <Button size="small" onClick={() => onFile(item)} disabled={disabled}>
                File to this case
              </Button>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default CaseActivityPanel;
